import { type SetStateAction, type Dispatch, useState } from 'react';
import type { IMeasure } from '../../Interfaces/ILegalTransactionMeasure';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { MeasuresList } from '../../LegalTransactionType/LegalTransactionMeasures/Measures/MeasuresList';
import { Box } from '@mui/material';
import { MeasureTaskCard } from './MeasureTaskCard';
import type { ILegalRequirementState } from '../../Interfaces/ILegalRequirementState';
import type { ILegalPhaseFullObject } from '../../Interfaces/ILegalPhase';
import type { ILegalTransaction_has_LegalTransactionSpecialFunction } from '../../Interfaces/ILegalTransaction_has_LegalTransactionSpecialFunction';
import type { ILegalTransactionGwGFullObject } from '../../Interfaces/ILegalTransactionGwG';

type IProps = {
  measures: IMeasure[];
  setMeasures: Dispatch<SetStateAction<IMeasure[]>>;
  idLegalTransaction: number;
  legalTransactionHasSpecialFunctions: ILegalTransaction_has_LegalTransactionSpecialFunction[];
  setLegalTransactionHasSpecialFunctions: Dispatch<
    SetStateAction<ILegalTransaction_has_LegalTransactionSpecialFunction[]>
  >;
  gwgs: ILegalTransactionGwGFullObject[];
  setGwgs: Dispatch<SetStateAction<ILegalTransactionGwGFullObject[]>>;
  legalPhaseFullObjects: ILegalPhaseFullObject[];
  legalRequirementStates: ILegalRequirementState[];
};

export const MeasureTab = ({
  measures,
  setMeasures,
  idLegalTransaction,
  legalTransactionHasSpecialFunctions,
  setLegalTransactionHasSpecialFunctions,
  gwgs,
  setGwgs,
  legalPhaseFullObjects,
  legalRequirementStates,
}: IProps) => {
  const [showEditMeasuresDialog, setShowEditMeasuresDialog] = useState(false);

  const [localMeasures, setLocalMeasures] = useState<IMeasure[]>(measures);

  const handleUpdateMeasure = (measure: IMeasure) => {
    setMeasures(prevMeasures =>
      prevMeasures.map(m =>
        m.idMeasurement === measure.idMeasurement ? measure : m
      )
    );
  };

  const handleUpdateMeasures = () => {
    setMeasures(localMeasures);
    setShowEditMeasuresDialog(false);
  };

  const legalRequirements = legalPhaseFullObjects.flatMap(
    legalPhase => legalPhase.LegalRequirementArray
  );

  return (
    <>
      <Box>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h5'>Vorgangsmanager</Typography>
          <Button
            variant='outlined'
            onClick={() => setShowEditMeasuresDialog(true)}
          >
            Anpassen
          </Button>
        </Box>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {measures.map(measure => (
            <MeasureTaskCard
              key={measure.idMeasurement}
              measure={measure}
              onUpdateMeasure={handleUpdateMeasure}
              idLegalTransaction={idLegalTransaction}
              legalTransactionHasSpecialFunctions={
                legalTransactionHasSpecialFunctions
              }
              setLegalTransactionHasSpecialFunctions={
                setLegalTransactionHasSpecialFunctions
              }
              gwgs={gwgs}
              setGwgs={setGwgs}
              legalPhaseFullObjects={legalPhaseFullObjects}
              legalRequirementStates={legalRequirementStates}
            />
          ))}
        </Box>
      </Box>

      <Dialog
        open={showEditMeasuresDialog}
        onClose={() => setShowEditMeasuresDialog(false)}
        fullWidth
        maxWidth='lg'
      >
        <DialogContent sx={{ minHeight: '60vh' }}>
          <MeasuresList
            measures={localMeasures}
            setMeasures={setLocalMeasures}
            legalRequirements={legalRequirements}
            legalRequirementTemplates={[]}
            setSpecialFunctions={setLegalTransactionHasSpecialFunctions}
            specialFunctions={legalTransactionHasSpecialFunctions}
            gwgs={gwgs}
            setGwgs={setGwgs}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            onClick={() => setShowEditMeasuresDialog(false)}
          >
            Abbrechen
          </Button>
          <Button variant='contained' onClick={handleUpdateMeasures}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
